import { writesApi } from './api';

// ── Registration funnel ────────────────────────────────────────────────────
// Merchants and riders no longer create their own accounts: they apply, a human
// reviews, and only on approval is an account provisioned and an invite sent.
// All of this is Spring Boot — the review decisions are writes, and the queue
// lives with the service that enforces the permissions.

export type ApplicantType = 'merchant' | 'rider';

export type ApplicationStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'withdrawn';

/**
 * Rows come straight from `registration_applications`, so snake_case. Renaming
 * in the client would mean two vocabularies for one row and a mapping layer to
 * keep in step.
 */
export interface RegistrationApplication {
  id: string;
  reference: string;
  applicant_type: ApplicantType | string;

  full_name: string;
  phone: string;
  email: string | null;
  location_text: string | null;

  business_name: string | null;
  business_type: string | null;
  vehicle_type: string | null;

  status: ApplicationStatus | string;
  /** `internal_bypass` = staff created this directly, skipping the queue. */
  source: 'public_form' | 'internal_bypass' | string;

  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  internal_notes: string | null;

  created_user_id: string | null;
  created_business_id: string | null;
  created_rider_id: string | null;
  invited_at: string | null;

  created_at: string;

  /**
   * Only present on the response that just approved it — never on a list.
   *
   * There is no SMS provider wired yet, so this is currently the ONLY way the
   * applicant gets their link. It must be shown to the reviewer at the moment
   * of approval, because it cannot be retrieved afterwards: the token is stored
   * hashed-by-purpose in verification_codes and nothing reads it back.
   */
  inviteLink?: string;
}

export interface CreateApplicationBody {
  applicantType: ApplicantType;
  fullName: string;
  phone: string;
  email?: string;
  locationText?: string;
  businessName?: string;
  businessType?: string;
  vehicleType?: string;
  notes?: string;
}

export const applicationService = {
  /** Omit `status` for the pending queue — the default and the common case. */
  list: (params?: { type?: string; status?: string; limit?: number }) =>
    writesApi.get<RegistrationApplication[]>(
      '/admin/applications',
      params as Record<string, unknown>,
    ),

  get: (id: string) => writesApi.get<RegistrationApplication>(`/admin/applications/${id}`),

  /** Provisions the account and returns the invite link. Irreversible. */
  approve: (id: string, reason?: string) =>
    writesApi.post<RegistrationApplication>(`/admin/applications/${id}/approve`, { reason }),

  reject: (id: string, reason: string) =>
    writesApi.post<RegistrationApplication>(`/admin/applications/${id}/reject`, { reason }),

  /**
   * Re-mint and resend the invite for an already-approved application — for a
   * link that expired (24 h) or a first send that never landed. Returns a fresh
   * `inviteLink`; the previous link is invalidated.
   */
  resendInvite: (id: string) =>
    writesApi.post<RegistrationApplication>(`/admin/applications/${id}/resend-invite`, {}),

  /** Bypass: create the account directly, skipping the queue. Same provisioning. */
  createDirect: (body: CreateApplicationBody) =>
    writesApi.post<RegistrationApplication>('/admin/applications/direct', body),
};

export function applicationStatusChip(status: string): string {
  switch (status) {
    case 'approved':
      return 'bg-[var(--state-success-soft,rgba(22,163,74,0.10))] text-[var(--state-success)]';
    case 'rejected':
      return 'bg-[var(--state-danger-soft,rgba(220,38,38,0.10))] text-[var(--state-danger)]';
    case 'withdrawn':
      return 'bg-[var(--bg-base)] text-[var(--text-muted)]';
    default:
      return 'bg-[var(--state-warning-soft,rgba(217,119,6,0.10))] text-[var(--state-warning,#d97706)]';
  }
}
